/**
 * DERAG10R2Service — Geocodificador L1.4 para infraestructura energética/medioambiental
 *
 * Busca infraestructuras DERA G10 (EDAR, ETAP, subestaciones, solares, eólicos,
 * RSU, puntos limpios…) almacenadas en R2 mediante fuzzy matching por nombre.
 *
 * Datos: DERA G10 (14 capas puntuales) — ~8.300 features
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /dera-g10/{ine5}.json.gz
 * Precisión: ±10 m (coordenadas oficiales DERA)
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 * @date 2026-02-16
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  DERAG10Feature,
  DERAG10SearchOptions,
  DERAG10Result,
} from '../../../types/deraG10';
import { G10_X_RANGE, G10_Y_RANGE, G10_PRECISION_M } from '../../../types/deraG10';
import type { GeocodingResult } from '../../../types/infrastructure';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;
const FETCH_TIMEOUT = 15_000;
const CACHE_MAX_ITEMS = 50;

/** Configuración uFuzzy para nombres españoles de infraestructura */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
};

// =============================================================================
// SINGLETON
// =============================================================================

let serviceInstance: DERAG10R2Service | null = null;

export function getDERAG10R2Service(): DERAG10R2Service {
  if (!serviceInstance) {
    serviceInstance = new DERAG10R2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetDERAG10R2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class DERAG10R2Service {
  private readonly baseUrl: string;
  private readonly cache = new Map<string, { data: DERAG10Feature[]; ts: number }>();
  private readonly loadingPromises = new Map<string, Promise<DERAG10Feature[]>>();
  private readonly fuzzy: uFuzzy;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? WORKER_BASE_URL;
    this.fuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  // ---------------------------------------------------------------------------
  // CARGA DE DATOS POR MUNICIPIO
  // ---------------------------------------------------------------------------

  /** Carga features G10 de un municipio desde R2 (con cache LRU) */
  async loadMunicipio(codINE: string): Promise<DERAG10Feature[]> {
    if (!/^\d{5}$/.test(codINE)) return [];

    // Cache hit
    const cached = this.cache.get(codINE);
    if (cached) return cached.data;

    // Dedup concurrent requests
    const pending = this.loadingPromises.get(codINE);
    if (pending) return pending;

    const promise = this.fetchMunicipio(codINE);
    this.loadingPromises.set(codINE, promise);

    try {
      const features = await promise;
      this.setCache(codINE, features);
      return features;
    } finally {
      this.loadingPromises.delete(codINE);
    }
  }

  private async fetchMunicipio(codINE: string): Promise<DERAG10Feature[]> {
    const url = `${this.baseUrl}/dera-g10/${codINE}.json.gz`;

    try {
      const { response } = await fetchWithBackoff(url, { timeoutMs: FETCH_TIMEOUT });

      if (response.status === 404) {
        // Municipio sin infraestructura G10 — normal
        return [];
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // El Worker puede devolver array directo o wrapper { features:[...] } / { data:[...] }
      const raw = await response.json();
      const data: DERAG10Feature[] = Array.isArray(raw)
        ? raw
        : (raw?.features ?? raw?.data ?? []);
      return data.filter(f => this.isValidCoord(f));
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.warn(`[DERAG10] Timeout cargando ${codINE}`);
      } else {
        console.error(`[DERAG10] Error cargando ${codINE}:`, error);
      }
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // GEOCODIFICACIÓN
  // ---------------------------------------------------------------------------

  /**
   * Geocodifica una infraestructura buscando en datos G10 R2.
   * Retorna el mejor resultado o null.
   */
  async geocode(options: DERAG10SearchOptions): Promise<GeocodingResult | null> {
    const results = await this.search(options);
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options.name);
  }

  /**
   * Busca infraestructuras G10 por nombre fuzzy y/o subtipo.
   * Retorna array ordenado por confianza descendente.
   */
  async search(options: DERAG10SearchOptions): Promise<DERAG10Result[]> {
    const { codINE, name, subtipo, minConfidence = 60, maxResults = 5 } = options;

    const features = await this.loadMunicipio(codINE);
    if (features.length === 0) return [];

    // Filtrar por subtipo si especificado
    let candidates = features;
    if (subtipo) {
      candidates = candidates.filter(
        f => f.subtipo.toUpperCase() === subtipo.toUpperCase()
      );
      if (candidates.length === 0) return [];
    }

    // Sin nombre → devolver candidatos filtrados
    if (!name?.trim()) {
      return candidates
        .slice(0, maxResults)
        .map(f => this.toResult(f, 70));
    }

    // Búsqueda fuzzy
    return this.fuzzySearch(candidates, name, minConfidence, maxResults);
  }

  /**
   * EDAR-b (DOP-1 c, Luis 1-nov-2026): busca en G10 el **testigo** de una fila hidráulica
   * (depuradora, potabilizadora, desaladora o depósito). El testigo nunca da coordenada: solo
   * sirve para confirmar la del plan (lo decide el *hook*). Sin clase hidráulica en el nombre
   * no se descarga nada.
   */
  async buscarTestigo(opciones: {
    codINE: string;
    nombre: string;
    direccion?: string;
    municipio: string;
  }): Promise<TestigoG10 | null> {
    if (!claseHidraulicaG10(opciones.nombre)) return null;
    const features = await this.loadMunicipio(opciones.codINE);
    if (features.length === 0) return null;
    return emparejaTestigoG10(features, opciones);
  }

  // ---------------------------------------------------------------------------
  // FUZZY MATCHING
  // ---------------------------------------------------------------------------

  private fuzzySearch(
    candidates: DERAG10Feature[],
    searchName: string,
    minConfidence: number,
    maxResults: number,
  ): DERAG10Result[] {
    const haystack = candidates.map(f => f.nombre);
    const normalizedName = searchName.toLowerCase().trim();
    const searchResult = this.fuzzy.search(haystack, normalizedName);

    if (!searchResult?.[0] || searchResult[0].length === 0) return [];

    const [idxs, _info, order] = searchResult;
    const processOrder = order ?? idxs;
    const results: DERAG10Result[] = [];

    for (let i = 0; i < Math.min(processOrder.length, maxResults * 2); i++) {
      const idx = processOrder[i];
      const featureIdx = idxs[idx];
      const feature = candidates[featureIdx];
      if (!feature) continue;

      const fuzzyScore = this.calcFuzzyScore(normalizedName, feature.nombre);
      const confidence = this.fuzzyScoreToConfidence(fuzzyScore);

      if (confidence >= minConfidence) {
        results.push(this.toResult(feature, confidence, fuzzyScore));
      }
      if (results.length >= maxResults) break;
    }

    return results;
  }

  /** Calcula score normalizado 0-1 del fuzzy match */
  private calcFuzzyScore(needle: string, haystack: string): number {
    const hLow = haystack.toLowerCase();
    if (hLow === needle) return 1.0;
    if (hLow.includes(needle)) return 0.95;
    if (needle.includes(hLow)) return 0.90;
    const ratio = needle.length / Math.max(hLow.length, needle.length);
    return Math.min(0.85, 0.5 + ratio * 0.4);
  }

  /** Convierte fuzzy score (0-1) a confianza (0-100) */
  private fuzzyScoreToConfidence(score: number): number {
    if (score >= 0.95) return 95;
    if (score >= 0.85) return 88;
    if (score >= 0.70) return 78;
    if (score >= 0.55) return 68;
    if (score >= 0.40) return 58;
    return 50;
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN
  // ---------------------------------------------------------------------------

  private toResult(
    feature: DERAG10Feature,
    confidence: number,
    fuzzyScore?: number,
  ): DERAG10Result {
    return {
      x: feature.x,
      y: feature.y,
      confidence,
      precision: G10_PRECISION_M,
      source: 'dera_g10_r2',
      feature,
      fuzzyScore,
    };
  }

  /** Convierte resultado G10 a GeocodingResult estándar */
  toGeocodingResult(result: DERAG10Result, originalQuery?: string): GeocodingResult {
    const f = result.feature;
    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'dera_g10_r2',
      sourceLayer: f.capa,
      originalQuery,
      matchedName: f.nombre,
      matchType: result.fuzzyScore != null ? 'fuzzy' : 'filter',
      fuzzyScore: result.fuzzyScore,
      municipality: f.municipio,
      muniCode: f.cod_ine,
      province: f.provincia,
      metadata: {
        id: f.id,
        subtipo: f.subtipo,
        capa: f.capa,
        precision: result.precision,
        fuente: 'DERA G10 Infraestructura Energética/Medioambiental',
        ...(f.meta ?? {}),
      },
    };
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES
  // ---------------------------------------------------------------------------

  private isValidCoord(f: DERAG10Feature): boolean {
    return (
      f.x >= G10_X_RANGE.min && f.x <= G10_X_RANGE.max &&
      f.y >= G10_Y_RANGE.min && f.y <= G10_Y_RANGE.max
    );
  }

  private setCache(codINE: string, data: DERAG10Feature[]): void {
    if (this.cache.size >= CACHE_MAX_ITEMS) {
      const oldest = this.cache.keys().next().value;
      if (oldest) this.cache.delete(oldest);
    }
    this.cache.set(codINE, { data, ts: Date.now() });
  }

  /** Limpia cache */
  clearCache(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /** Carga datos directamente (para tests sin fetch) */
  loadFromData(codINE: string, features: DERAG10Feature[]): void {
    const valid = features.filter(f => this.isValidCoord(f));
    this.cache.set(codINE, { data: valid, ts: Date.now() });
  }

  /** Estadísticas */
  get cacheSize(): number { return this.cache.size; }
}

// =============================================================================
// EDAR-b · TESTIGO G10 DE FILAS HIDRÁULICAS
// =============================================================================
//
// Emparejador «V2» de la tarea EDAR (`.ptel/p0_edar_20261101/contrafactual_g10.py`, medido sobre
// el corpus y verificado en PNOA): siglas normalizadas («E.D.A.R.» = «EDAR»), fichas del nombre y
// la dirección de la fila contra el nombre, el paraje (`situacion`) y la procedencia de G10, y
// candidato único del mismo tipo cuando la fila no trae fichas propias. Empate, o varios
// candidatos sin nada en común → ninguno. DOP-2 a: un depósito solo casa con entradas ETAP de
// tipo «Depósitos» (con toda la capa, 12 depósitos del corpus casaban con potabilizadoras o con
// la desaladora; Jubrique #21, a 95 m de la suya).

/** Clase hidráulica que el nombre de la fila declara. */
export type ClaseHidraulicaG10 = 'EDAR' | 'ETAP' | 'DESALADORA' | 'DEPOSITO';

/** Instalación de G10 que hace de testigo de una fila hidráulica. */
export interface TestigoG10 {
  x: number;
  y: number;
  id: string;
  nombre: string;
  clase: ClaseHidraulicaG10;
  /** `paraje`: fichas en común con la fila · `unico`: único candidato del tipo en el municipio */
  modo: 'paraje' | 'unico';
  fichasComunes: string[];
}

const VACIAS_G10 = new Set(
  'de del la las el los y en a al s n sn paraje camino carretera ctra km pk poligono parcela acceso por'.split(' ')
);
const GENERICAS_G10 = new Set(
  ('edar etap depuradora depuradoras potabilizadora potabilizadoras desaladora planta estacion ' +
    'aguas residuales residual tratamiento agua potable deposito depositos municipal saneamiento red ' +
    'rio rambla nucleo entrada').split(' ')
);

/** Minúsculas, sin tildes ni signos, con las siglas «E.D.A.R.» y «E.T.A.P.» juntas. */
export function normalizaG10(s: string | null | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[^\x00-\x7f]/g, '')
    .toLowerCase()
    .replace(/\be\.?\s*d\.?\s*a\.?\s*r\.?(?=\W|$)/g, ' edar ')
    .replace(/\be\.?\s*t\.?\s*a\.?\s*p\.?(?=\W|$)/g, ' etap ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Clase hidráulica por el nombre de la fila, o `null` si no lo es. */
export function claseHidraulicaG10(nombre: string | null | undefined): ClaseHidraulicaG10 | null {
  const n = normalizaG10(nombre);
  if (/\b(edar|depurador|aguas residuales)/.test(n)) return 'EDAR';
  if (/\bdesalad/.test(n)) return 'DESALADORA';
  if (/\b(etap|potabiliz)/.test(n)) return 'ETAP';
  if (/\bdeposito/.test(n)) return 'DEPOSITO';
  return null;
}

function fichasG10(s: string | number | null | undefined): Set<string> {
  return new Set(
    normalizaG10(s == null ? '' : String(s))
      .split(' ')
      .filter(t => t.length > 2 && !VACIAS_G10.has(t) && !GENERICAS_G10.has(t) && !/^\d+$/.test(t))
  );
}

/** ¿Difieren en como mucho una letra? Solo para fichas de 4 letras o más («Hoica» / «Oica»). */
function difiereEnUnaLetra(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.min(a.length, b.length) < 4 || Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    let distintas = 0;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) distintas++;
    return distintas === 1;
  }
  const [corta, larga] = a.length < b.length ? [a, b] : [b, a];
  let i = 0;
  while (i < corta.length && corta[i] === larga[i]) i++;
  return corta.slice(i) === larga.slice(i + 1);
}

/** Candidatos de G10 del mismo tipo que la fila. */
export function candidatosTestigoG10(
  features: DERAG10Feature[],
  clase: ClaseHidraulicaG10
): DERAG10Feature[] {
  if (clase === 'EDAR') return features.filter(f => f.subtipo === 'EDAR');
  const etap = features.filter(f => f.subtipo === 'ETAP');
  if (clase === 'DEPOSITO') return etap.filter(f => f.metadata?.tipo === 'Depósitos');
  if (clase === 'DESALADORA') {
    const desaladoras = etap.filter(f => /\bdesal/.test(normalizaG10(f.nombre)));
    return desaladoras.length > 0 ? desaladoras : etap;
  }
  return etap;
}

type ModoEleccion = 'paraje' | 'empate' | 'unico_sin_fichas' | 'varios_sin_fichas' | 'sin_candidato';

function eligeCandidato(
  candidatos: DERAG10Feature[],
  fila: { nombre: string; direccion?: string; municipio: string },
  municipioSoloEnNombre: boolean
): { feature: DERAG10Feature | null; modo: ModoEleccion; comunes: string[] } {
  const delMunicipio = new Set(normalizaG10(fila.municipio).split(' '));
  const deDireccion = [...fichasG10(fila.direccion)].filter(
    t => !municipioSoloEnNombre || !delMunicipio.has(t)
  );
  const q = new Set([...fichasG10(fila.nombre), ...deDireccion]);
  const puntuados = candidatos
    .map(f => {
      const h = [
        ...fichasG10(f.nombre),
        ...fichasG10(f.metadata?.situacion),
        ...fichasG10(f.metadata?.procedenci),
      ];
      const comunes = [...q].filter(t => h.some(u => difiereEnUnaLetra(t, u))).sort();
      return { f, comunes };
    })
    .sort((a, b) => b.comunes.length - a.comunes.length);
  if (puntuados.length === 0) return { feature: null, modo: 'sin_candidato', comunes: [] };
  const [primero, segundo] = puntuados;
  if (primero.comunes.length > 0) {
    return !segundo || segundo.comunes.length < primero.comunes.length
      ? { feature: primero.f, modo: 'paraje', comunes: primero.comunes }
      : { feature: null, modo: 'empate', comunes: primero.comunes };
  }
  return puntuados.length === 1
    ? { feature: primero.f, modo: 'unico_sin_fichas', comunes: [] }
    : { feature: null, modo: 'varios_sin_fichas', comunes: [] };
}

/**
 * Testigo G10 de una fila hidráulica entre las instalaciones G10 de su municipio, o `null`.
 * El nombre del municipio solo cuenta como ficha si va en el NOMBRE de la fila (Guadix #90
 * «E.D.A.R. Pp6» con «… 18500 Guadix» en la dirección casaba con la EDAR principal a 5,9 km).
 */
export function emparejaTestigoG10(
  features: DERAG10Feature[],
  fila: { nombre: string; direccion?: string; municipio: string }
): TestigoG10 | null {
  const clase = claseHidraulicaG10(fila.nombre);
  if (!clase) return null;
  const candidatos = candidatosTestigoG10(features, clase);
  const porParaje = eligeCandidato(candidatos, fila, true);
  const elegido = porParaje.modo === 'paraje'
    ? { ...porParaje, modo: 'paraje' as const }
    : (() => {
        const r = eligeCandidato(candidatos, fila, false);
        return r.modo === 'unico_sin_fichas' ? { ...r, modo: 'unico' as const } : null;
      })();
  if (!elegido?.feature) return null;
  const f = elegido.feature;
  return { x: f.x, y: f.y, id: f.id, nombre: f.nombre, clase, modo: elegido.modo, fichasComunes: elegido.comunes };
}

export default DERAG10R2Service;
